import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PillarBadge from "@/components/PillarBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Trash2, Wallet, Pencil } from "lucide-react";
import { toast } from "sonner";
import PayoutDialog from "@/components/PayoutDialog";
import HostProfileEditor from "@/components/HostProfileEditor";
import { money } from "@/lib/currency";

function AvailabilityEditor({ exp, onUpdate }) {
  const [mode, setMode] = useState(exp.availability_mode || "block");
  const [dates, setDates] = useState(exp.unavailable_dates || []);
  const [windows, setWindows] = useState(exp.available_windows || []);
  const [newDate, setNewDate] = useState("");
  const [winStart, setWinStart] = useState("");
  const [winEnd, setWinEnd] = useState("");

  const save = async (patch = {}) => {
    const body = { availability_mode: mode, unavailable_dates: dates, available_windows: windows, ...patch };
    await api.put(`/experiences/${exp.id}/availability`, body);
    onUpdate?.();
  };

  const changeMode = async (m) => {
    setMode(m);
    await save({ availability_mode: m });
    toast.success(`Mode: ${m === "block" ? "Block dates" : "Open windows"}`);
  };

  const addDate = async () => {
    if (!newDate || dates.includes(newDate)) return;
    const next = [...dates, newDate].sort(); setDates(next); setNewDate("");
    await save({ unavailable_dates: next });
    toast.success("Blocked");
  };
  const removeDate = async (d) => {
    const next = dates.filter((x) => x !== d); setDates(next);
    await save({ unavailable_dates: next });
  };
  const addWindow = async () => {
    if (!winStart || !winEnd || winStart > winEnd) return;
    const next = [...windows, { start: winStart, end: winEnd }]; setWindows(next);
    setWinStart(""); setWinEnd("");
    await save({ available_windows: next });
    toast.success("Window added");
  };
  const removeWindow = async (i) => {
    const next = windows.filter((_, idx) => idx !== i); setWindows(next);
    await save({ available_windows: next });
  };

  return (
    <div className="p-4 border-t border-subtle">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-xs text-muted2">Mode</span>
        <div className="inline-flex rounded-full border border-subtle bg-canvas p-0.5 text-xs">
          <button data-testid={`mode-block-${exp.id}`} onClick={() => changeMode("block")} className={`px-3 py-1 rounded-full ${mode === "block" ? "bg-moss text-canvas" : ""}`}>Block dates</button>
          <button data-testid={`mode-open-${exp.id}`} onClick={() => changeMode("open")} className={`px-3 py-1 rounded-full ${mode === "open" ? "bg-moss text-canvas" : ""}`}>Open windows</button>
        </div>
      </div>
      {mode === "block" ? (
        <>
          <div className="flex gap-2 mb-2">
            <Input data-testid={`avail-input-${exp.id}`} type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)} className="rounded-lg bg-white border-subtle text-xs" />
            <button data-testid={`avail-add-${exp.id}`} onClick={addDate} className="rounded-lg bg-moss text-canvas text-xs px-3 hover:bg-moss-hover">Block</button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {dates.length === 0 && <span className="text-xs text-muted2">All dates open</span>}
            {dates.map((d) => (
              <span key={d} className="inline-flex items-center gap-1 text-xs bg-terracotta/10 text-terracotta rounded-full px-2 py-1">
                {d}<button onClick={() => removeDate(d)} className="hover:text-destructive">×</button>
              </span>
            ))}
          </div>
        </>
      ) : (
        <>
          <div className="flex gap-2 mb-2 flex-wrap">
            <Input type="date" value={winStart} onChange={(e) => setWinStart(e.target.value)} className="rounded-lg bg-white border-subtle text-xs flex-1 min-w-[120px]" />
            <Input type="date" value={winEnd} onChange={(e) => setWinEnd(e.target.value)} className="rounded-lg bg-white border-subtle text-xs flex-1 min-w-[120px]" />
            <button data-testid={`window-add-${exp.id}`} onClick={addWindow} className="rounded-lg bg-moss text-canvas text-xs px-3 hover:bg-moss-hover">Open</button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {windows.length === 0 && <span className="text-xs text-muted2">No windows — nothing bookable</span>}
            {windows.map((w, i) => (
              <span key={`${w.start}-${w.end}-${i}`} className="inline-flex items-center gap-1 text-xs bg-moss/10 text-moss rounded-full px-2 py-1">
                {w.start} → {w.end}<button onClick={() => removeWindow(i)} className="hover:text-destructive">×</button>
              </span>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default function HostDashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [experiences, setExperiences] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [payoutOpen, setPayoutOpen] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login"); return; }
    if (user.role !== "host") { navigate("/traveler"); return; }
    api.get("/experiences/mine").then((r) => setExperiences(r.data));
    api.get("/bookings/mine").then((r) => setBookings(r.data));
  }, [user, loading, navigate]);

  const removeExp = async (id) => {
    if (!confirm("Delete this experience?")) return;
    await api.delete(`/experiences/${id}`);
    setExperiences((x) => x.filter((e) => e.id !== id));
    toast.success("Experience removed");
  };

  if (!user) return null;
  const totalRevenue = bookings.filter(b => b.payment_status === "paid").reduce((s, b) => s + b.total_price, 0);

  return (
    <div className="min-h-screen bg-[#F4F2EC]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div>
            <div className="overline">Host</div>
            <h1 className="font-serif text-4xl md:text-5xl mt-1">Welcome, {user.name}</h1>
          </div>
          <Button data-testid="new-experience-button" onClick={() => navigate("/host/new")} className="rounded-full bg-moss hover:bg-moss-hover text-canvas px-6">
            <Plus className="mr-2 h-4 w-4" /> New experience
          </Button>
        </div>
        <div className="mb-8">
          <Button data-testid="open-payout" onClick={() => setPayoutOpen(true)} variant="outline" className="rounded-full bg-white border-subtle hover:border-moss">
            <Wallet className="mr-2 h-4 w-4" /> Payout & KYC
          </Button>
        </div>

        <div className="mb-10">
          <HostProfileEditor />
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
          <div className="bg-white border border-subtle rounded-2xl p-6">
            <div className="overline">Listings</div>
            <div className="font-serif text-4xl mt-2" data-testid="stat-listings">{experiences.length}</div>
          </div>
          <div className="bg-white border border-subtle rounded-2xl p-6">
            <div className="overline">Total bookings</div>
            <div className="font-serif text-4xl mt-2" data-testid="stat-bookings">{bookings.length}</div>
          </div>
          <div className="bg-white border border-subtle rounded-2xl p-6">
            <div className="overline">Confirmed</div>
            <div className="font-serif text-4xl mt-2" data-testid="stat-confirmed">{bookings.filter(b => b.status === "confirmed").length}</div>
          </div>
          <div className="bg-white border border-subtle rounded-2xl p-6">
            <div className="overline">Revenue</div>
            <div className="font-serif text-4xl mt-2" data-testid="stat-revenue">{money(totalRevenue)}</div>
          </div>
        </div>

        {/* Listings */}
        <h2 className="font-serif text-3xl mb-5">Your listings</h2>
        {experiences.length === 0 ? (
          <div className="bg-white border border-subtle rounded-2xl p-10 text-center">
            <p className="text-muted2 mb-4">You haven't listed any experiences yet.</p>
            <Button data-testid="empty-new-experience" onClick={() => navigate("/host/new")} className="rounded-full bg-moss text-canvas">
              Create your first
            </Button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {experiences.map((e) => (
              <div key={e.id} className="bg-white border border-subtle rounded-2xl overflow-hidden">
                <Link to={`/experience/${e.id}`}>
                  <div className="aspect-[4/3] overflow-hidden">
                    <img src={e.images?.[0]} alt="" className="w-full h-full object-cover" />
                  </div>
                </Link>
                <div className="p-5">
                  <div className="flex flex-wrap gap-1 mb-2">
                    {e.pillars?.slice(0, 2).map(p => <PillarBadge key={p} pillar={p} />)}
                    {e.verified ? (
                      <span className="pillar-tag bg-moss/10 text-moss">✓ Verified</span>
                    ) : (
                      <span className="pillar-tag bg-terracotta/10 text-terracotta">Pending review</span>
                    )}
                  </div>
                  <div className="font-serif text-xl leading-tight">{e.title}</div>
                  <div className="text-xs text-muted2 mt-1">{e.location} · {money(e.price_usd)}/guest</div>
                  <div className="mt-4 flex justify-between items-center">
                    <Link to={`/experience/${e.id}`} className="text-sm text-moss underline">View</Link>
                    <div className="flex items-center gap-3">
                      <button
                        data-testid={`edit-exp-${e.id}`}
                        onClick={() => navigate(`/host/edit/${e.id}`)}
                        className="text-muted2 hover:text-moss inline-flex items-center gap-1 text-sm"
                        title="Edit listing"
                      >
                        <Pencil className="h-4 w-4" strokeWidth={1.75} />
                        Edit
                      </button>
                      <button data-testid={`delete-exp-${e.id}`} onClick={() => removeExp(e.id)} className="text-muted2 hover:text-destructive" title="Delete listing">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
                <AvailabilityEditor exp={e} />
              </div>
            ))}
          </div>
        )}

        {/* Bookings */}
        <h2 className="font-serif text-3xl mb-5 mt-14">Bookings coming in</h2>
        {bookings.length === 0 ? (
          <div className="text-muted2 text-sm">No bookings yet.</div>
        ) : (
          <div className="bg-white border border-subtle rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-canvas">
                <tr>
                  <th className="text-left p-4 font-medium">Traveler</th>
                  <th className="text-left p-4 font-medium">Experience</th>
                  <th className="text-left p-4 font-medium">Check-in</th>
                  <th className="text-left p-4 font-medium">Guests</th>
                  <th className="text-left p-4 font-medium">Total</th>
                  <th className="text-left p-4 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {bookings.map((b) => (
                  <tr key={b.id} className="border-t border-subtle" data-testid={`booking-row-${b.id}`}>
                    <td className="p-4">{b.traveler_name}</td>
                    <td className="p-4">{b.experience_title}</td>
                    <td className="p-4">{b.check_in}</td>
                    <td className="p-4">{b.guests}</td>
                    <td className="p-4">{money(b.total_price)}</td>
                    <td className="p-4">
                      <span className={`pillar-tag ${b.status === "confirmed" ? "bg-moss/10 text-moss" : "bg-subtle text-muted2"}`}>
                        {b.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <PayoutDialog open={payoutOpen} onOpenChange={setPayoutOpen} />
      <Footer />
    </div>
  );
}
